import { useMemo, useState } from 'react';
import { FileText, PencilLine, ArrowRight, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import BankStatementImport from '@/components/import/BankStatementImport';
import { useProfile } from '@/hooks/useProfile';
import { useTransactions, type Currency } from '@/hooks/useTransactions';
import { toast } from '@/hooks/use-toast';
import {
  calculateMonthlySummary,
  calculateFocoScore,
} from '@/lib/foco-calculations';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export interface ActivationResult {
  year: number;
  month: number;
  /** Precomputed score (manual path). When absent, WOW derives it live (PDF path). */
  score?: number;
}

interface OnboardingActivationProps {
  open: boolean;
  onActivated: (result: ActivationResult) => void;
  onSkip: () => void;
}

function parseAmount(raw: string): number {
  const n = parseFloat(raw.replace(/[^\d.,]/g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Determine the month with the most transactions from a list of ISO dates. */
function dominantMonth(dates: { date: string }[]): { year: number; month: number } {
  const counts = new Map<string, number>();
  for (const { date } of dates) {
    const key = date.slice(0, 7); // YYYY-MM
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = -1;
  for (const [key, count] of counts) {
    if (count > bestCount) {
      best = key;
      bestCount = count;
    }
  }
  const now = new Date();
  if (!best) return { year: now.getFullYear(), month: now.getMonth() + 1 };
  const [y, m] = best.split('-').map(Number);
  return { year: y, month: m };
}

export function OnboardingActivation({ open, onActivated, onSkip }: OnboardingActivationProps) {
  const { profile } = useProfile();
  const { bulkCreateTransactions } = useTransactions();
  const currency = (profile?.default_currency ?? 'CRC') as Currency;

  const now = new Date();
  const [mode, setMode] = useState<'choose' | 'manual'>('choose');
  const [income, setIncome] = useState('');
  const [fixed, setFixed] = useState('');
  const [variable, setVariable] = useState('');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [saving, setSaving] = useState(false);

  const years = useMemo(() => [now.getFullYear() - 1, now.getFullYear()], [now]);

  const handleManualSubmit = async () => {
    const incomeAmount = parseAmount(income);
    if (incomeAmount <= 0) {
      toast({
        title: 'Falta tu ingreso',
        description: 'Ingresa al menos tu ingreso mensual aproximado para calcular tu score.',
        variant: 'destructive',
      });
      return;
    }
    const fixedAmount = parseAmount(fixed);
    const variableAmount = parseAmount(variable);
    const dateStr = `${year}-${String(month).padStart(2, '0')}-01`;

    const toCreate = [
      { type: 'income' as const, category: 'salary', amount: incomeAmount, notes: 'Ingreso mensual (activación)' },
      { type: 'fixed_expense' as const, category: 'other_fixed', amount: fixedAmount, notes: 'Gastos fijos (activación)' },
      { type: 'variable_expense' as const, category: 'other', amount: variableAmount, notes: 'Gastos variables (activación)' },
    ].filter((t) => t.amount > 0);

    setSaving(true);
    try {
      await bulkCreateTransactions.mutateAsync(
        toCreate.map((t) => ({
          date: dateStr,
          type: t.type,
          category: t.category,
          amount: t.amount,
          notes: t.notes,
          payment_method: 'transfer' as const,
          currency,
        })),
      );

      const summary = calculateMonthlySummary(
        incomeAmount,
        fixedAmount,
        variableAmount,
        0,
        0,
        0,
      );
      const score = calculateFocoScore(summary, []);
      onActivated({ year, month, score });
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudieron guardar tus datos. Intenta de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-lg gap-4 max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton
      >
        <DialogHeader>
          <DialogTitle className="text-xl">
            ¡Listo! Ahora hagamos que tu FOCO Score no empiece en cero.
          </DialogTitle>
          <DialogDescription>Toma menos de 2 minutos.</DialogDescription>
        </DialogHeader>

        {mode === 'choose' ? (
          <div className="space-y-3">
            <div className="rounded-xl border p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm">Subir mi estado de cuenta en PDF</p>
                  <p className="text-xs text-muted-foreground">
                    La IA analiza todo automáticamente. No consume tu importación gratuita.
                  </p>
                </div>
              </div>
              <BankStatementImport
                variant="default"
                countTowardsLimit={false}
                onImportComplete={(txs) => {
                  if (!txs.length) return;
                  onActivated(dominantMonth(txs));
                }}
              />
            </div>

            <div className="rounded-xl border p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                  <PencilLine className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm">Ingresar un dato rápido ahora</p>
                  <p className="text-xs text-muted-foreground">
                    Solo necesito unos números para empezar.
                  </p>
                </div>
              </div>
              <Button type="button" variant="secondary" className="w-full gap-2" onClick={() => setMode('manual')}>
                Ingresar manualmente
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>

            <button
              type="button"
              onClick={onSkip}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
            >
              Prefiero explorar primero →
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="act-month">Mes</Label>
                <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                  <SelectTrigger id="act-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_NAMES.map((name, i) => (
                      <SelectItem key={name} value={String(i + 1)}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="act-year">Año</Label>
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                  <SelectTrigger id="act-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="act-income">💰 ¿Cuánto dinero ingresa a tu cuenta cada mes?</Label>
              <Input
                id="act-income"
                inputMode="decimal"
                placeholder="Ej. 1.200.000"
                value={income}
                onChange={(e) => setIncome(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="act-fixed">🏠 ¿Cuánto pagas fijo al mes? (alquiler, préstamos, servicios)</Label>
              <Input
                id="act-fixed"
                inputMode="decimal"
                placeholder="Ej. 500.000"
                value={fixed}
                onChange={(e) => setFixed(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="act-variable">📊 ¿Cuánto estimas que gastas en total al mes? (variable)</Label>
              <Input
                id="act-variable"
                inputMode="decimal"
                placeholder="Ej. 300.000"
                value={variable}
                onChange={(e) => setVariable(e.target.value)}
              />
            </div>

            <p className="text-xs text-muted-foreground">
              Guardaremos estos datos como transacciones de {MONTH_NAMES[month - 1]} {year}. Podrás editarlos
              luego en Transacciones.
            </p>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
              <Button type="button" variant="ghost" onClick={() => setMode('choose')} disabled={saving}>
                Volver
              </Button>
              <Button type="button" className="gap-2" onClick={handleManualSubmit} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {saving ? 'Calculando…' : 'Calcular mi FOCO Score'}
                {!saving && <ArrowRight className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
